import { useCallback, useEffect, useMemo, useState } from 'react';
import { PriceCards } from '../../../components/PriceCards/PriceCards';
import { QUARTERLY_PRICE_CHART_CURRENTLY_HOVERED } from '../../../constants';
import { useTranslation } from '../../../i18n/i18n';
import { useOfferContext } from '../../../OfferContext/OfferContext';
import { getCurrentDataIndex } from '../QuarterlyPriceChart/QuarterlyPriceChart.helpers';

function isCustomEvent(event: Event): event is CustomEvent {
  return 'detail' in event;
}

export const QuarterlyPriceCards = () => {
  const { t } = useTranslation();
  const { quarterlyPriceData } = useOfferContext();
  const [currentQuarterlyPrice, setCurrentQuarterlyPrice] = useState<number>(0);

  const onQuarterlyPriceHover = useCallback(
    (event: Event) => {
      if (isCustomEvent(event) && quarterlyPriceData) {
        const newIndex = parseInt(event.detail);
        if (!isNaN(newIndex) && quarterlyPriceData[newIndex]) {
          const newPrice = parseFloat(quarterlyPriceData[newIndex].value);
          setCurrentQuarterlyPrice(newPrice);
        }
      }
    },
    [quarterlyPriceData],
  );

  useEffect(() => {
    window.addEventListener(QUARTERLY_PRICE_CHART_CURRENTLY_HOVERED, onQuarterlyPriceHover);
    return () => {
      window.removeEventListener(QUARTERLY_PRICE_CHART_CURRENTLY_HOVERED, onQuarterlyPriceHover);
    };
  }, [onQuarterlyPriceHover]);

  const averagePrice = useMemo(() => {
    if (!quarterlyPriceData?.length) return '';

    if (currentQuarterlyPrice === 0) {
      const currentIndex = getCurrentDataIndex(quarterlyPriceData);
      setCurrentQuarterlyPrice(parseFloat(quarterlyPriceData[currentIndex].value));
    }

    let totalPrice = 0;
    quarterlyPriceData.forEach(({ value }) => (totalPrice += parseFloat(value) * 100));
    const newAverage = totalPrice / quarterlyPriceData.length;

    return `<strong>${Number(newAverage.toFixed(2)).toLocaleString()}</strong> ${t('centPerKwh')}`;
  }, [quarterlyPriceData, t, currentQuarterlyPrice]);

  const cards = useMemo(() => {
    if (!quarterlyPriceData?.length) return [];

    const newCurrentQuarterlyPrice = Number(
      (currentQuarterlyPrice * 100).toFixed(2),
    ).toLocaleString();

    return [
      {
        title: `<strong>${newCurrentQuarterlyPrice}</strong> ${t('centPerKwh')}`,
        caption: t('chart.marketPrice'),
        key: 'currentPrice',
      },
      {
        title: averagePrice,
        caption: t('chart.averagePrice'),
        key: 'averagePrice',
      },
    ];
  }, [quarterlyPriceData, currentQuarterlyPrice, t]);

  return <PriceCards cards={cards} flexRows withMargin dataTestId="quarterly-price-cards" />;
};
